import { forwardRef, useMemo, useState } from 'react';

import { RiCalendarLine, RiErrorWarningLine } from 'react-icons/ri';

import {
  YearPickerInput as MantineYearPickerInput,
  YearPickerInputProps
} from '@mantine/dates';

import { useMergeClassNames } from '../../utils';

import { YearPickerInputOverrideClassNames } from './YearPickerInput.styles';

/**
 * Editable date input
 * Use DatePicker input to prevent user from editing, it also support range, multi select
 */
const YearPickerInput = forwardRef<
  HTMLButtonElement,
  YearPickerInputProps<'default'>
>(
  (
    {
      size = 'md',
      error,
      leftSection,
      rightSection,
      valueFormat = 'YYYY',
      classNames,
      onFocus,
      onBlur,
      ...rest
    },
    ref
  ) => {
    const rightSectionFinal = error ? (
      <RiErrorWarningLine />
    ) : rightSection ? (
      rightSection
    ) : undefined;
    const [focussed, setFocussed] = useState<boolean>(false);

    const classes = useMemo(() => {
      return YearPickerInputOverrideClassNames({
        size,
        focussed,
        error,
        rightSection: rightSectionFinal
      });
    }, [size, focussed, error, rightSectionFinal]);

    const handleFocus = (e: React.FocusEvent<HTMLButtonElement, Element>) => {
      setFocussed(true);
      onFocus && onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLButtonElement, Element>) => {
      setFocussed(false);
      onBlur && onBlur(e);
    };

    const mergedClassNames = useMergeClassNames({
      classNames: [classes, classNames]
    });

    return (
      <MantineYearPickerInput<'default'>
        {...rest}
        ref={ref}
        leftSection={leftSection ? leftSection : <RiCalendarLine />}
        size={size}
        error={error}
        rightSection={rightSectionFinal}
        valueFormat={valueFormat}
        onFocus={handleFocus}
        onBlur={handleBlur}
        classNames={mergedClassNames}
      />
    );
  }
);

export { YearPickerInput };
export type { YearPickerInputProps };
